/**
 * Reusable Button component with multiple variants and sizes
 */

import React from 'react';
import { clsx } from 'clsx';
import { Loader2 } from 'lucide-react';

const Button = React.forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  className = '',
  onClick,
  type = 'button',
  ...props
}, ref) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variantClasses = {
    primary: 'bg-primary-600 hover:bg-primary-700 text-white focus:ring-primary-500 shadow-sm hover:shadow-md',
    secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-900 focus:ring-gray-500',
    outline: 'border border-gray-300 hover:bg-gray-50 text-gray-700 focus:ring-gray-500',
    ghost: 'hover:bg-gray-100 text-gray-700 focus:ring-gray-500',
    success: 'bg-success hover:bg-green-600 text-white focus:ring-green-500 shadow-sm hover:shadow-md',
    warning: 'bg-warning hover:bg-yellow-600 text-white focus:ring-yellow-500 shadow-sm hover:shadow-md',
    danger: 'bg-error hover:bg-red-600 text-white focus:ring-red-500 shadow-sm hover:shadow-md',
    info: 'bg-info hover:bg-blue-600 text-white focus:ring-blue-500 shadow-sm hover:shadow-md',
  };
  
  const sizeClasses = {
    xs: 'px-2.5 py-1.5 text-xs rounded-md',
    sm: 'px-3 py-2 text-sm rounded-md',
    md: 'px-4 py-2.5 text-sm rounded-lg',
    lg: 'px-6 py-3 text-base rounded-lg',
    xl: 'px-8 py-4 text-lg rounded-xl',
  };

  const isDisabled = disabled || loading;

  const handleClick = (e) => {
    if (isDisabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  return (
    <button
      ref={ref}
      type={type}
      className={clsx(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && 'w-full',
        isDisabled && 'cursor-not-allowed',
        className
      )}
      disabled={isDisabled}
      onClick={handleClick}
      {...props}
    >
      {/* Left icon */}
      {leftIcon && !loading && (
        <span className="mr-2 flex-shrink-0">
          {leftIcon}
        </span>
      )}
      
      {/* Loading spinner */}
      {loading && (
        <Loader2 className={clsx(
          'animate-spin flex-shrink-0',
          children ? 'mr-2' : '',
          size === 'xs' ? 'w-3 h-3' :
          size === 'sm' ? 'w-4 h-4' :
          size === 'md' ? 'w-4 h-4' :
          size === 'lg' ? 'w-5 h-5' :
          'w-6 h-6'
        )} />
      )}
      
      {/* Button text */}
      {children && (
        <span className={clsx(loading && 'opacity-70')}>
          {children}
        </span>
      )}
      
      {/* Right icon */}
      {rightIcon && !loading && (
        <span className="ml-2 flex-shrink-0">
          {rightIcon}
        </span>
      )}
    </button>
  );
});

Button.displayName = 'Button';

// Button group component for related actions
export const ButtonGroup = ({ children, className = '', ...props }) => (
  <div 
    className={clsx(
      'inline-flex rounded-lg shadow-sm',
      className
    )}
    {...props}
  >
    {React.Children.map(children, (child, index) => {
      if (!React.isValidElement(child)) return child;
      
      return React.cloneElement(child, {
        className: clsx(
          child.props.className,
          index === 0 && 'rounded-r-none',
          index === React.Children.count(children) - 1 && 'rounded-l-none',
          index > 0 && index < React.Children.count(children) - 1 && 'rounded-none',
          index > 0 && 'border-l-0'
        ),
      });
    })}
  </div>
);

// Icon button component
export const IconButton = React.forwardRef(({
  icon,
  variant = 'ghost',
  size = 'md',
  tooltip,
  className = '',
  ...props
}, ref) => {
  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-7 h-7',
  };

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={clsx('p-2', className)}
      title={tooltip}
      {...props}
    >
      {React.isValidElement(icon) 
        ? React.cloneElement(icon, { 
            className: clsx(icon.props.className, iconSizes[size])
          })
        : icon
      }
    </Button>
  );
});

IconButton.displayName = 'IconButton';

export default Button;
